/**
 * Admin Clients Form Component
 * Form for creating and editing client accounts
 * Access: Admin only via /admin/clients
 */

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
    Container,
    Paper,
    TextField,
    Button,
    Box,
    Alert,
    CircularProgress,
    Typography,
    Card,
    CardContent,
    Grid,
    Divider,
    Switch,
    FormControlLabel
} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import RefreshIcon from '@mui/icons-material/Refresh';
import clientsAdminService from '../../services/clientsAdminService';
import { useLoader } from '../../context/LoaderContext';

// Limits (K11 pts 1 and 3): a workspace made here is Production; a free trial comes from the sign-up form. Storage is
// shown and entered in GB.
const GB = 1073741824;
const EMPTY_FORM = {
    workspaceName: '',
    domain: '',
    adminEmail: '',
    slug: '',
    status: 'ACTIVE',
    maxTrialUsers: '',
    maxTrialContents: '',
    maxStorageGb: '',
    trialEndsAt: '',
    isActive: false,
    first_name: '',
    last_name: ''
};
const LIMITS_HINT = {
    FREE_TRIAL: 'Free trial: nothing can be added past these limits, and access stops on the end date.',
    ACTIVE: "Production: the plan's numbers. Crossing one alerts Beetsol; nothing is blocked."
};

const ClientsAdminForm = ({ clientId: propClientId = null, onSuccess = null }) => {
    const { clientId: urlClientId } = useParams();
    const navigate = useNavigate();
    
    // Use URL param if available, otherwise use prop
    const clientId = urlClientId || propClientId;
    
    const [formData, setFormData] = useState(EMPTY_FORM);
    // The workspace as saved: its type (a Production workspace can't go back to a free trial) and whether it is a trial
    // waiting for approval (its dates start when it is approved).
    const [saved, setSaved] = useState({ status: null, waiting: false });

    const [errors, setErrors] = useState({});
    const [loading, setLoading] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [successMessage, setSuccessMessage] = useState('');
    const [errorMessage, setErrorMessage] = useState('');
    const [slugAvailable, setSlugAvailable] = useState(null);
    const [domainAvailable, setDomainAvailable] = useState(null);
    const { startLoader, stopLoader } = useLoader();

    // Load client data if editing
    useEffect(() => {
        console.log('Client ID:', clientId);
        if (clientId) {
            loadClientData();
        }
    }, [clientId]);

    const loadClientData = async () => {
        try {
            setLoading(true);
            const response = await clientsAdminService.getClientById(clientId);
            const client = response.data.data;

            // Load admin user info
            let adminFirstName = '';
            let adminLastName = '';
             try {
                const adminResponse = await clientsAdminService.getClientAdmin(clientId);
                if (adminResponse.data.data) {
                    adminFirstName = adminResponse.data.data.firstName;
                    adminLastName = adminResponse.data.data.lastName;
                }
            } catch (adminError) {
                console.log('Admin info not available:', adminError);
            }
            setFormData({
                workspaceName: client.company_name,
                domain: client.domain,
                adminEmail: client.admin_email,
                slug: client.slug,
                status: client.status,
                maxTrialUsers: client.max_trial_users,
                maxTrialContents: client.max_trial_contents,
                maxStorageGb: client.max_free_trial_size ? Math.round(Number(client.max_free_trial_size) / GB) : '',
                trialEndsAt: client.trial_expires_at ? String(client.trial_expires_at).slice(0, 10) : '',
                isActive: client.is_active === 1,
                first_name: adminFirstName,
                last_name: adminLastName
            });
            setSaved({ status: client.status, waiting: client.status === 'FREE_TRIAL' && !client.trial_expires_at });
        } catch (error) {
            setErrorMessage('Failed to load client data');
            console.error(error);
        } finally {
            setLoading(false);
        }
    };

    // Generate slug from workspace name
    const handleWorkspaceNameChange = (e) => {
        const value = e.target.value;
        setFormData({ ...formData, workspaceName: value });

            if (value.trim() && !clientId) {
                const fetchSlug = async () => {
                const slugResp = await clientsAdminService.generateUniqueSlug(value);
                const slug = slugResp.data.slug;
                console.log('Generated slug:', slug);
                setFormData(prev => ({ ...prev, slug }));
                checkSlugAvailability(slug);
            };

            fetchSlug();
        }
    };

    // Validate domain and email match
    const validateDomainEmailMatch = (domain, email) => {
        const emailDomain = email.split('@')[1]?.toLowerCase() || '';
        const cleanDomain = domain.replace(/^(www\.)/, '').toLowerCase();
        return emailDomain === cleanDomain;
    };

    // Check domain availability
    const checkDomainAvailability = async (domain) => {
        try {
            const isAvailable = await clientsAdminService.checkDomainAvailability(domain);
            setDomainAvailable(isAvailable);
        } catch (error) {
            console.error('Error checking domain availability:', error);
        }
    };

    // Check slug availability
    const checkSlugAvailability = async (slug) => {
        try {
            const response = await clientsAdminService.getAllClients({ search: slug });
            const exists = response.data.data.some(client => client.slug === slug && client.id !== clientId);
            setSlugAvailable(!exists);
        } catch (error) {
            console.error('Error checking slug availability:', error);
        }
    };

    // Validate form
    const validateForm = () => {
        const newErrors = {};

        if (!formData.workspaceName.trim()) {
            newErrors.workspaceName = 'Workspace name is required';
        }

        if (!formData.domain.trim()) {
            newErrors.domain = 'Domain is required';
        }

        if (!formData.adminEmail.trim()) {
            newErrors.adminEmail = 'Admin email is required';
        } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.adminEmail)) {
            newErrors.adminEmail = 'Invalid email format';
        } else if (!validateDomainEmailMatch(formData.domain, formData.adminEmail)) {
            newErrors.adminEmail = 'Email domain must match primary domain';
        }

        if (!formData.slug.trim()) {
            newErrors.slug = 'Slug is required';
        } else if (slugAvailable === false) {
            newErrors.slug = 'This slug is already taken. Try another.';
        }
        
        if (domainAvailable === false && !clientId) {
            newErrors.domain = 'This domain is already registered';
        }

        // The three limits are required (K11 pt 3): a workspace made here starts with them empty.
        for (const field of ['maxTrialUsers', 'maxTrialContents', 'maxStorageGb']) {
            const n = Number(formData[field]);
            if (!Number.isInteger(n) || n < 1) {
                newErrors[field] = 'Must be a number greater than 0';
            }
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    // Handle form submission
    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!validateForm()) {
            setErrorMessage('Please fix the errors below');
            return;
        }

        setSubmitting(true);
        setErrorMessage('');
        setSuccessMessage('');
        startLoader();

        try {
            // Removed 17 Oct 2026 (K11 pt 4): Max Total Added, which nothing checks any more; the column stays until N45 pt 5.1.
            const submitData = {
                workspaceName: formData.workspaceName,
                domain: formData.domain,
                adminEmail: formData.adminEmail,
                first_name: formData.first_name,
                last_name: formData.last_name,
                max_trial_users: Number(formData.maxTrialUsers),
                max_trial_contents: Number(formData.maxTrialContents),
                max_storage_gb: Number(formData.maxStorageGb)
            };

            if (clientId) {
                await clientsAdminService.updateClient(clientId, {
                    ...submitData,
                    status: formData.status,
                    // The end date of an approved free trial; a waiting trial's dates start when it is approved.
                    ...(formData.status === 'FREE_TRIAL' && !saved.waiting ? { trial_expires_at: formData.trialEndsAt } : {}),
                    is_active: formData.isActive ? 1 : 0
                });
                setSuccessMessage('Client updated successfully');
                loadClientData();
            } else {
                await clientsAdminService.createClient(submitData);
                setSuccessMessage('Client created successfully!');
                stopLoader();
                setFormData(EMPTY_FORM);
            }
            stopLoader();
            if (onSuccess) {
                onSuccess();
            }
        } catch (error) {
            console.error('Error submitting form:', error);
            setErrorMessage(
                error.response?.data?.message ||
                'An error occurred while processing your request'
            );
        } finally {
            setSubmitting(false);
        }
    };

    // Handle reset
    const handleReset = () => {
        if (clientId) {
            loadClientData();
        } else {
            setFormData(EMPTY_FORM);
        }
        setErrors({});
        setErrorMessage('');
        setSuccessMessage('');
    };

    if (loading) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Container maxWidth="md" sx={{ py: 4 }}>
            <Paper sx={{ p: 4 }}>
                <Typography variant="h4" gutterBottom sx={{ mb: 3 }}>
                    {clientId ? 'Edit Client' : 'Create New Client'}
                </Typography>

                {successMessage && (
                    <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccessMessage('')}>
                        {successMessage}
                    </Alert>
                )}

                {errorMessage && (
                    <Alert severity="error" sx={{ mb: 2, backgroundColor:'#A32D2D' }} onClose={() => setErrorMessage('')}>
                        {errorMessage}
                    </Alert>
                )}

                <form onSubmit={handleSubmit}>
                    {/* Basic Information */}
                    <Typography variant="h6" sx={{ mt: 3, mb: 2 }}>
                        Basic Information
                    </Typography>

                    <Grid container spacing={2} sx={{ mb: 3 }}>
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                label="Workspace Name"
                                value={formData.workspaceName}
                                onChange={handleWorkspaceNameChange}
                                error={!!errors.workspaceName}
                                helperText={errors.workspaceName}
                                placeholder="e.g., Acme Corporation"
                                disabled={submitting}
                            />
                        </Grid>

                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                label="Primary Domain"
                                value={formData.domain}
                                onChange={(e) => {
                                    setFormData({ ...formData, domain: e.target.value });
                                    if (e.target.value) {
                                        checkDomainAvailability(e.target.value);
                                    }
                                }}
                                error={!!errors.domain}
                                helperText={
                                    errors.domain ||
                                    (domainAvailable === false
                                        ? 'Domain already registered'
                                        : domainAvailable === true
                                        ? 'Domain is available'
                                        : '')
                                        
                                }
                                FormHelperTextProps={{sx: {'&.Mui-error': {color: '#fe0a2d'}}}}
                                sx={{
                                    '& .MuiOutlinedInput-root.Mui-error .MuiOutlinedInput-notchedOutline': {
                                        borderColor: '#fe0a2d',
                                    },
                                }}
                                placeholder="e.g., acmecorp.com"
                                disabled={submitting || !!clientId}
                            />
                        </Grid>

                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                label="Admin Email"
                                type="email"
                                value={formData.adminEmail}
                                onChange={(e) => setFormData({ ...formData, adminEmail: e.target.value })}
                                error={!!errors.adminEmail}
                                helperText={
                                    errors.adminEmail ||
                                    'Must match the domain (e.g., admin@acmecorp.com)'
                                }
                                FormHelperTextProps={{sx: {'&.Mui-error': {color: '#fe0a2d'}}}}
                                sx={{
                                    '& .MuiOutlinedInput-root.Mui-error .MuiOutlinedInput-notchedOutline': {
                                        borderColor: '#fe0a2d',
                                    },
                                }}
                                placeholder="admin@acmecorp.com"
                                disabled={submitting}
                            />
                        </Grid>
                        <Grid item xs={6}>
                            <TextField
                                fullWidth
                                label="First Name"
                                type="text"
                                value={formData.first_name}
                                onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
                                error={!!errors.first_name}
                                helperText={
                                    errors.first_name ||
                                    'Admin user first name'
                                }
                                FormHelperTextProps={{sx: {'&.Mui-error': {color: '#fe0a2d'}}}}
                                sx={{
                                    '& .MuiOutlinedInput-root.Mui-error .MuiOutlinedInput-notchedOutline': {
                                        borderColor: '#fe0a2d',
                                    },
                                }}
                                placeholder="John"
                                disabled={submitting}
                            />
                        </Grid>
                        <Grid item xs={6}>
                            <TextField
                                fullWidth
                                label="Last Name"
                                type="text"
                                value={formData.last_name}
                                onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
                                error={!!errors.last_name}
                                helperText={
                                    errors.last_name ||
                                    'Admin user last name'
                                }
                                FormHelperTextProps={{sx: {'&.Mui-error': {color: '#fe0a2d'}}}}
                                sx={{
                                    '& .MuiOutlinedInput-root.Mui-error .MuiOutlinedInput-notchedOutline': {
                                        borderColor: '#fe0a2d',
                                    },
                                }}
                                placeholder="Doe"
                                disabled={submitting}
                            />
                        </Grid>
                    </Grid>

                    {/* Slug Information */}
                    <Divider sx={{ my: 3 }} />
                    <Typography variant="h6" sx={{ mt: 3, mb: 2 }}>
                        URL Slug (Auto-generated)
                    </Typography>

                    <Grid container spacing={2} sx={{ mb: 3 }}>
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                label="Slug"
                                value={formData.slug}
                                onChange={(e) => {
                                    setFormData({ ...formData, slug: e.target.value });
                                    checkSlugAvailability(e.target.value);
                                }}
                                error={!!errors.slug}
                                helperText={
                                    errors.slug ||
                                    (slugAvailable === false
                                        ? 'Slug already taken'
                                        : slugAvailable === true
                                        ? 'Slug is available'
                                        : 'URL-friendly identifier (auto-generated from workspace name)')
                                }
                                FormHelperTextProps={{sx: {'&.Mui-error': {color: '#fe0a2d'}}}}
                                sx={{
                                    '& .MuiOutlinedInput-root.Mui-error .MuiOutlinedInput-notchedOutline': {
                                        borderColor: '#fe0a2d',
                                    },
                                }}
                                placeholder="workspace-slug"
                                disabled={submitting || !!clientId}
                            />
                        </Grid>
                    </Grid>

                    {/* Limits (K11 pts 1 and 3). Removed 17 Oct 2026 (K11): Trial Settings, its trial defaults for a workspace
                        made here, Max Total Added, and the Status list (Expired and Cancelled included). */}
                    <Divider sx={{ my: 3 }} />
                    <Typography variant="h6" sx={{ mt: 3, mb: 1 }}>
                        Limits
                    </Typography>
                    <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2 }}>
                        {LIMITS_HINT[formData.status === 'FREE_TRIAL' ? 'FREE_TRIAL' : 'ACTIVE']}
                    </Typography>

                    <Grid container spacing={2} sx={{ mb: 3 }}>
                        <Grid item xs={12} sm={6}>
                            {/* Free trial or Production. A Production workspace, Beetsol's own included, can't go back
                                to a free trial, and a workspace made here is Production. */}
                            {clientId && saved.status === 'FREE_TRIAL' && String(clientId) !== '1' ? (
                                <TextField
                                    fullWidth
                                    label="Type"
                                    select
                                    value={formData.status}
                                    onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                                    disabled={submitting}
                                    SelectProps={{ native: true }}
                                >
                                    <option value="FREE_TRIAL">Free trial</option>
                                    <option value="ACTIVE">Production</option>
                                </TextField>
                            ) : (
                                <TextField fullWidth label="Type" value={formData.status === 'FREE_TRIAL' ? 'Free trial' : 'Production'} disabled />
                            )}
                        </Grid>

                        {formData.status === 'FREE_TRIAL' ? (
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    fullWidth
                                    label="Trial ends"
                                    type="date"
                                    value={formData.trialEndsAt}
                                    onChange={(e) => setFormData({ ...formData, trialEndsAt: e.target.value })}
                                    disabled={submitting || saved.waiting}
                                    helperText={saved.waiting ? 'Starts when approved' : ''}
                                    InputLabelProps={{ shrink: true }}
                                />
                            </Grid>
                        ) : (
                            <Grid item xs={12} sm={6} />
                        )}

                        <Grid item xs={12} sm={4}>
                            <TextField
                                fullWidth
                                required
                                label="Users"
                                type="number"
                                value={formData.maxTrialUsers}
                                onChange={(e) => setFormData({ ...formData, maxTrialUsers: e.target.value })}
                                error={!!errors.maxTrialUsers}
                                helperText={errors.maxTrialUsers}
                                disabled={submitting}
                                InputProps={{ inputProps: { min: 1 } }}
                                InputLabelProps={{ shrink: true }}
                            />
                        </Grid>

                        <Grid item xs={12} sm={4}>
                            <TextField
                                fullWidth
                                required
                                label="Content items"
                                type="number"
                                value={formData.maxTrialContents}
                                onChange={(e) => setFormData({ ...formData, maxTrialContents: e.target.value })}
                                error={!!errors.maxTrialContents}
                                helperText={errors.maxTrialContents}
                                disabled={submitting}
                                InputProps={{ inputProps: { min: 1 } }}
                                InputLabelProps={{ shrink: true }}
                            />
                        </Grid>

                        <Grid item xs={12} sm={4}>
                            <TextField
                                fullWidth
                                required
                                label="Storage (GB)"
                                type="number"
                                value={formData.maxStorageGb}
                                onChange={(e) => setFormData({ ...formData, maxStorageGb: e.target.value })}
                                error={!!errors.maxStorageGb}
                                helperText={errors.maxStorageGb}
                                disabled={submitting}
                                InputProps={{ inputProps: { min: 1 } }}
                                InputLabelProps={{ shrink: true }}
                            />
                        </Grid>
                    </Grid>

                    {/* Status */}
                    {clientId && (
                        <>
                            <Divider sx={{ my: 3 }} />
                            <Typography variant="h6" sx={{ mt: 3, mb: 2 }}>
                                Account Status
                            </Typography>

                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={formData.isActive}
                                        onChange={(e) =>
                                            setFormData({ ...formData, isActive: e.target.checked })
                                        }
                                        // Beetsol's own workspace stays Active (S-14); a waiting trial is switched on by
                                        // Approve on the list (K11).
                                        disabled={submitting || String(clientId) === '1' || saved.waiting}
                                    />
                                }
                                label={formData.isActive ? 'Active' : 'Inactive'}
                            />
                        </>
                    )}

                    {/* Form Actions */}
                    <Box sx={{ mt: 4, display: 'flex', gap: 2 }}>
                        <Button
                            type="submit"
                            variant="contained"
                            color="primary"
                            startIcon={<SaveIcon />}
                            disabled={submitting}
                        >
                            {submitting ? 'Saving...' : clientId ? 'Update Client' : 'Create Client'}
                        </Button>

                        <Button
                            type="button"
                            variant="outlined"
                            color="secondary"
                            startIcon={<RefreshIcon />}
                            onClick={handleReset}
                            disabled={submitting}
                        >
                            Reset
                        </Button>
                    </Box>                    
                </form>
            </Paper>
        </Container>
    );
};

export default ClientsAdminForm;
